import { useEffect, useRef, useState } from "react";
import {
    listHiddenOriginDeploymentViews,
    subscribeTenantViewCloud,
    unhideCloudTenantView,
} from "../lib/tenantViewCloudStore.js";

export default function HiddenOriginDeploymentViews({ kind }) {
    const [rows, setRows] = useState(
        () => listHiddenOriginDeploymentViews(null, kind),
    );
    const [mutationError, setMutationError] = useState(false);
    const [pendingIds, setPendingIds] = useState(() => new Set());
    const pendingIdsRef = useRef(new Set());

    useEffect(() => {
        const refresh = () => setRows(listHiddenOriginDeploymentViews(null, kind));
        refresh();
        return subscribeTenantViewCloud(refresh);
    }, [kind]);

    const unhide = async (row) => {
        if (pendingIdsRef.current.has(row.tenantViewId)) return;
        pendingIdsRef.current.add(row.tenantViewId);
        setPendingIds(new Set(pendingIdsRef.current));
        setMutationError(false);
        try {
            await unhideCloudTenantView(row);
        } catch {
            setMutationError(true);
        } finally {
            pendingIdsRef.current.delete(row.tenantViewId);
            setPendingIds(new Set(pendingIdsRef.current));
        }
    };

    if (rows.length === 0) return null;

    return (
        <section
            aria-label={`Hidden official ${kind}s`}
            aria-busy={pendingIds.size > 0 ? "true" : undefined}
            className="mx-auto mt-[16px] grid w-full max-w-[880px] gap-[8px] border border-[color:var(--line)] bg-[color:var(--bg)] p-[12px]"
        >
            <div className="grid gap-[2px]">
                <span className="text-[11px] font-medium uppercase tracking-[0.08em] text-[color:var(--muted)]">
                    Hidden official {kind}s
                </span>
                <span className="text-[10px] leading-[1.4] text-[color:var(--muted-soft)]">
                    Hidden deployments stay available here until you unhide them.
                </span>
            </div>

            {rows.map((row) => (
                <article
                    key={row.tenantViewId}
                    className="grid gap-[7px] border-t border-[color:var(--line)] pt-[8px]"
                >
                    <div className="flex flex-wrap items-start justify-between gap-[8px]">
                        <div className="grid min-w-0 gap-[2px]">
                            <span className="text-[12px] font-medium text-[color:var(--ink)]">
                                {row.title || row.command || row.slug || `Official ${kind}`}
                            </span>
                            <code
                                title={row.tenantViewId}
                                className="break-all text-[9.5px] text-[color:var(--muted-soft)]"
                            >
                                {row.tenantViewId}
                            </code>
                        </div>
                        <span className="border border-[color:var(--line)] px-[6px] py-[2px] text-[9px] uppercase tracking-[0.06em] text-[color:var(--muted)]">
                            Hidden
                        </span>
                    </div>

                    <button
                        type="button"
                        disabled={!row.canUnhide || pendingIds.has(row.tenantViewId)}
                        onClick={() => void unhide(row)}
                        className="w-fit border border-[color:var(--line)] px-[8px] py-[4px] text-[11px] disabled:opacity-50"
                    >
                        {pendingIds.has(row.tenantViewId) ? "Unhiding…" : "Unhide"}
                    </button>
                </article>
            ))}

            {mutationError ? (
                <p role="alert" className="m-0 text-[11px] text-[color:var(--danger)]">
                    The official view couldn’t be unhidden.
                </p>
            ) : null}
        </section>
    );
}
